/**
 * PublicAsset Page
 * Displays asset information for public (unauthenticated) users
 * Accessed via QR code scan with asset token
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiMapPin, FiCheckCircle, FiClock } from 'react-icons/fi';
import PublicLayout from '../components/layout/PublicLayout';
import apiService from '../services/api';
import { PublicAssetInfo } from '../types';
import { toast } from '../utils/toast';
import Spinner from '../components/common/Spinner';

const PublicAsset: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [assetInfo, setAssetInfo] = useState<PublicAssetInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid QR code');
      setLoading(false);
      return;
    }

    loadAssetInfo();
  }, [token]);

  const loadAssetInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getPublicAsset(token!);
      setAssetInfo(data);
    } catch (err: any) {
      console.error('Failed to load asset info:', err);
      setError(err.response?.data?.message || 'Asset not found. Please check your QR code.');
      toast.error('Failed to load asset information');
    } finally {
      setLoading(false);
    }
  };

  const handleReportIssue = () => {
    navigate(`/public/report/${token}`);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !assetInfo) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <FiAlertCircle className="w-6 h-6 text-red-600 mr-3 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-medium text-red-900 mb-2">Asset Not Found</h3>
                <p className="text-red-700">{error || 'Unable to load asset information.'}</p>
                <p className="text-sm text-red-600 mt-2">
                  Please contact the asset owner if you believe this is an error.
                </p>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const { asset, recent_issues } = assetInfo;

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto">
        {/* Asset Header */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{asset.name}</h1>
            {asset.description && (
              <p className="text-gray-600 text-lg mb-4">{asset.description}</p>
            )}

            {asset.location && (
              <div className="flex items-center text-gray-600">
                <FiMapPin className="w-5 h-5 mr-2" />
                <span>{asset.location}</span>
              </div>
            )}

            {/* Status Badge */}
            <div className="mt-4">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  asset.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : asset.status === 'maintenance'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {asset.status === 'active' && <FiCheckCircle className="w-4 h-4 mr-1" />}
                {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
              </span>
            </div>
          </div>

          {/* Report Issue Button */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={handleReportIssue}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-xs focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Report an Issue
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Found a problem with this asset? Let us know and we'll address it quickly.
            </p>
          </div>
        </div>

        {/* Recent Issues */}
        {recent_issues && recent_issues.length > 0 && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Issues Resolved</h2>
              <p className="text-sm text-gray-600 mt-1">
                See what we've fixed recently
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {recent_issues.map((issue, index) => (
                <div key={index} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{issue.title}</h3>
                      <div className="flex items-center mt-2 space-x-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            issue.status === 'resolved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {issue.status === 'resolved' && <FiCheckCircle className="w-3 h-3 mr-1" />}
                          {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                        </span>
                        <div className="flex items-center text-xs text-gray-500">
                          <FiClock className="w-3 h-3 mr-1" />
                          {new Date(issue.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            This asset is maintained by {asset.name.split(' ')[0] || 'our team'}.
            Your reports help us keep things running smoothly.
          </p>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PublicAsset;
